import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * BottleReveal — a tall, scroll-driven section featuring the Victory bottle.
 * As the viewer scrolls through, the bottle rises, tilts subtly, and settles
 * center-frame while pill-shaped graphic annotations, bubble mark orbits,
 * and outline serif copy sweep across the canvas. Sits between the plant
 * hero and the manifesto to give the client's real product its own moment.
 */
const BottleReveal = ({ bottleUrl }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    // Bottle motion — trimmed set of transforms for perf
    const bottleY = useTransform(scrollYProgress, [0, 1], ["25%", "-25%"]);
    const bottleScale = useTransform(scrollYProgress, [0, 1], [0.9, 1.05]);

    // Graphic elements — combined into fewer transforms
    const wordSweep = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
    const wordSweep2 = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
    const arcOffset = useTransform(scrollYProgress, [0, 1], [180, -180]);

    return (
        <section
            ref={ref}
            id="bottle-reveal"
            className="relative isolate min-h-[180vh] overflow-hidden bg-[#7a0e0e] text-ivory-100"
            data-testid="bottle-reveal"
        >
            {/* Deep crimson layered background with radial glow */}
            <div
                className="absolute inset-0 -z-10"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 30%, #b91c1c 0%, #7a0e0e 45%, #3a0505 100%)",
                }}
            />
            <div className="grain absolute inset-0 -z-10" />

            {/* Sticky viewport frame */}
            <div className="sticky top-0 flex h-screen w-full items-center justify-center overflow-hidden">
                {/* Sweep of huge serif word behind bottle */}
                <motion.div
                    style={{ x: wordSweep }}
                    className="pointer-events-none absolute inset-x-0 top-[18%] flex justify-center"
                >
                    <span
                        className="stroke-serif text-[24vw] font-light leading-none tracking-tightest text-ivory-100/40 md:text-[18vw]"
                        aria-hidden="true"
                    >
                        VICTORY
                    </span>
                </motion.div>

                {/* Concentric arcs / rings — static SVG (no per-frame rotation) */}
                <svg
                    aria-hidden="true"
                    viewBox="0 0 800 800"
                    className="pointer-events-none absolute h-[130vh] w-[130vh] opacity-60"
                >
                    <defs>
                        <linearGradient id="ringGrad" x1="0" x2="1" y1="0" y2="1">
                            <stop offset="0%" stopColor="#F8F5EE" stopOpacity="0.6" />
                            <stop offset="60%" stopColor="#F8F5EE" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <circle
                        cx="400"
                        cy="400"
                        r="360"
                        fill="none"
                        stroke="url(#ringGrad)"
                        strokeWidth="1"
                        strokeDasharray="2 8"
                    />
                    <circle
                        cx="400"
                        cy="400"
                        r="280"
                        fill="none"
                        stroke="#F8F5EE"
                        strokeOpacity="0.18"
                        strokeWidth="1"
                    />
                    <circle
                        cx="400"
                        cy="400"
                        r="200"
                        fill="none"
                        stroke="#F8F5EE"
                        strokeOpacity="0.12"
                        strokeWidth="1"
                    />
                </svg>

                {/* Bottle — treated as a premium gradient background element */}
                <motion.div
                    style={{
                        y: bottleY,
                        scale: bottleScale,
                    }}
                    className="pointer-events-none absolute inset-0 z-10 flex h-full items-center justify-center will-change-transform"
                >
                    <div className="relative">
                        {/* Under-bottle glow / floor puddle */}
                        <div
                            aria-hidden="true"
                            className="absolute -bottom-6 left-1/2 h-16 w-64 -translate-x-1/2 rounded-full blur-xl md:w-96"
                            style={{
                                background:
                                    "radial-gradient(ellipse at center, rgba(255,220,190,0.35) 0%, rgba(0,0,0,0) 70%)",
                            }}
                        />
                        <img
                            src={bottleUrl}
                            alt="Victory Cola bottle"
                            loading="lazy"
                            decoding="async"
                            className="relative z-10 max-h-[92vh] w-auto"
                            style={{
                                WebkitMaskImage:
                                    "radial-gradient(ellipse 55% 80% at 50% 55%, black 40%, transparent 92%)",
                                maskImage:
                                    "radial-gradient(ellipse 55% 80% at 50% 55%, black 40%, transparent 92%)",
                                opacity: 0.72,
                                mixBlendMode: "luminosity",
                            }}
                            data-testid="bottle-reveal-image"
                            draggable={false}
                        />
                    </div>
                </motion.div>

                {/* Annotation pills — left */}
                <motion.div
                    style={{ y: arcOffset }}
                    className="pointer-events-none absolute left-6 top-1/2 z-30 -translate-y-1/2 md:left-16"
                >
                    <Annotation
                        num="01"
                        label="200 ML · PET"
                        note="Signature Victory silhouette · sculpted grip lines"
                    />
                </motion.div>

                {/* Annotation pills — right (removed by client request) */}

                {/* Bottom sweeping word */}
                <motion.div
                    style={{ x: wordSweep2 }}
                    className="pointer-events-none absolute bottom-[6%] left-0 right-0 flex justify-center"
                >
                    <span className="stroke-serif text-[20vw] leading-none tracking-tightest text-ivory-100/25 md:text-[14vw]">
                        cola.
                    </span>
                </motion.div>

                {/* Top-left brand tag */}
                <div className="absolute left-6 top-6 z-30 flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-ivory-100/70 md:left-12 md:top-10">
                    <span className="h-px w-6 bg-ivory-100/60" />
                    Specimen 001 · Victory Cola
                </div>

                {/* Corner reg-marks */}
                <CornerMark className="left-4 bottom-4" />
                <CornerMark className="right-4 bottom-4 rotate-180" />

                {/* Bottom-right note */}
                <div className="absolute bottom-8 right-6 z-30 max-w-xs text-right text-[10px] uppercase tracking-[0.35em] text-ivory-100/60 md:right-12">
                    · Bottled at our own plant · Blowing → RFC → Label →
                    Packing
                </div>
            </div>

            {/* Below-the-fold caption paragraph — reveals as user scrolls past sticky */}
            <div className="relative z-10 mx-auto max-w-[1400px] px-6 pb-32 pt-16 md:px-12">
                <motion.h3
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40%" }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="font-serif-display max-w-[16ch] text-6xl leading-[0.95] tracking-tightest text-ivory-100 md:text-8xl"
                >
                    Meet the bottle
                    <br />
                    <em className="italic text-ivory-100/80">
                        that will taste the win.
                    </em>
                </motion.h3>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-30%" }}
                    transition={{ duration: 0.9, delay: 0.2 }}
                    className="mt-8 max-w-xl text-base leading-[1.7] text-ivory-100/85 md:text-lg"
                >
                    Every Victory Cola bottle is engineered on our own line — a
                    shape drawn for grip, a label pulled tight to the last
                    contour, and a formula sealed the moment it&apos;s born.
                </motion.p>

                {/* Flavour lineup teaser */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-20%" }}
                    transition={{ duration: 0.9, delay: 0.35 }}
                    className="mt-10 flex flex-wrap items-center gap-3"
                    data-testid="flavour-lineup"
                >
                    <span className="text-[10px] uppercase tracking-[0.4em] text-ivory-100/60">
                        · The lineup
                    </span>
                    <FlavourChip label="Victory Cola" active />
                    <FlavourChip label="Hot-Fill Juice" />
                    <FlavourChip label="More Flavours" muted />
                </motion.div>
            </div>
        </section>
    );
};

const Annotation = ({ num, label, note, align = "left" }) => (
    <div
        className={`flex max-w-[220px] flex-col gap-2 ${align === "right" ? "items-end text-right" : "items-start text-left"}`}
    >
        <span className="rounded-full border border-ivory-100/40 px-3 py-1 text-[10px] uppercase tracking-[0.35em] text-ivory-100/80">
            · {num}
        </span>
        <span className="font-serif-display text-xl leading-tight text-ivory-100 md:text-2xl">
            {label}
        </span>
        <span className="text-[10px] uppercase tracking-[0.3em] text-ivory-100/60">
            {note}
        </span>
    </div>
);

const FlavourChip = ({ label, active = false, muted = false }) => {
    const base =
        "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.3em] transition-colors";
    if (active)
        return (
            <span
                className={`${base} border-ivory-100 bg-ivory-100 text-crimson-800`}
            >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-crimson-800" />
                {label}
            </span>
        );
    if (muted)
        return (
            <span
                className={`${base} border-ivory-100/25 text-ivory-100/50`}
            >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-ivory-100/40" />
                {label}
            </span>
        );
    return (
        <span
            className={`${base} border-ivory-100/60 text-ivory-100 hover:bg-ivory-100/10`}
        >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-ivory-100/70" />
            {label}
        </span>
    );
};

const CornerMark = ({ className = "" }) => (
    <div
        className={`absolute z-30 h-4 w-4 border-l border-t border-ivory-100/70 ${className}`}
        aria-hidden="true"
    />
);

export default BottleReveal;
